import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { getMyWallet, withdraw, getMyWithdrawHistory } from "apis";
import { Loading } from "components";
import { useAppModal } from "components/common/AppModalProvider";
import { useUserStore } from "store";
import { WalletWithdrawStatus } from "types";

const BANKS = [
  "KB국민", "신한", "우리", "하나", "NH농협",
  "IBK기업", "카카오뱅크", "토스뱅크", "SC제일", "씨티",
  "대구", "부산", "광주", "경남", "전북",
  "제주", "수협", "우체국", "새마을금고", "신협",
];

const formatResidentNumber = (val: string) => {
  const nums = val.replace(/[^0-9]/g, "").slice(0, 13);
  if (nums.length <= 6) return nums;
  return `${nums.slice(0, 6)}-${nums.slice(6)}`;
};

export default function StudioWallet() {
  const queryClient = useQueryClient();
  const { showAlert, showConfirm } = useAppModal();
  const { user, setUser } = useUserStore();

  // Wallet balance
  const { data: walletData, isLoading } = useQuery(
    ["studioWallet"],
    () => getMyWallet()
  );

  // Withdraw history
  const { data: historyData } = useQuery(
    ["myWithdrawHistory"],
    () => getMyWithdrawHistory()
  );
  const withdrawHistory = historyData?.data?.data || [];
  const walletBalance = walletData?.data?.data?.klav ?? 0;

  // Withdrawal state
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [showKycModal, setShowKycModal] = useState(false);

  // KYC form
  const [kycForm, setKycForm] = useState({
    realName: user?.realName || "",
    residentNumber: user?.residentNumber || "",
    bankName: user?.bankName || "",
    accountNumber: user?.accountNumber || "",
    accountHolder: user?.accountHolder || "",
  });

  useEffect(() => {
    if (user) {
      setKycForm({
        realName: user.realName || "",
        residentNumber: user.residentNumber || "",
        bankName: user.bankName || "",
        accountNumber: user.accountNumber || "",
        accountHolder: user.accountHolder || "",
      });
    }
  }, [user]);

  const hasKyc = !!(user?.realName && user?.bankName && user?.accountNumber && user?.accountHolder);

  // Fee calculation (플랫폼 수수료 15%는 서비스 완료 적립 시 이미 선차감됨)
  const amount = parseInt(withdrawAmount) || 0;
  const tax = Math.round(amount * 0.033);
  const netAmount = amount - tax;

  // KYC save
  const saveKyc = async () => {
    if (!kycForm.realName || !kycForm.bankName || !kycForm.accountNumber || !kycForm.accountHolder) {
      showAlert("모든 정산 정보를 입력해주세요.", { variant: "warning" });
      return;
    }
    try {
      const { patch } = await import("utils");
      const baseUrl = `${process.env.REACT_APP_API_URL}/user/update`;
      const formData = new FormData();
      formData.append("realName", kycForm.realName);
      formData.append("residentNumber", kycForm.residentNumber);
      formData.append("bankName", kycForm.bankName);
      formData.append("accountNumber", kycForm.accountNumber);
      formData.append("accountHolder", kycForm.accountHolder);
      const response: any = await patch(baseUrl, formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (response.data.isSuccess === "T") {
        setUser(response.data.data);
        setShowKycModal(false);
        showAlert("정산 정보가 저장되었습니다.", { variant: "success" });
      }
    } catch (err) {
      showAlert("정산 정보 저장에 실패했습니다.", { variant: "error" });
    }
  };

  // Withdrawal request
  const withdrawMutation = useMutation(
    (amt: number) => withdraw(amt),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["studioWallet"]);
        queryClient.invalidateQueries(["myWithdrawHistory"]);
        setWithdrawAmount("");
        showAlert(`출금 신청이 완료되었습니다.\n실수령액: ${netAmount.toLocaleString()}원`, { variant: "success" });
      },
      onError: (err: any) => {
        const msg = err?.response?.data?.message || "출금 신청에 실패했습니다.";
        showAlert(msg, { variant: "error" });
      },
    }
  );

  const handleWithdraw = async () => {
    if (amount < 10000) {
      showAlert("최소 출금 금액은 10,000원입니다.", { variant: "warning" });
      return;
    }
    if (amount > walletBalance) {
      showAlert("잔액이 부족합니다.", { variant: "warning" });
      return;
    }
    if (!hasKyc) {
      setShowKycModal(true);
      return;
    }
    const confirmed = await showConfirm(
      `${amount.toLocaleString()}원을 출금 신청하시겠습니까?\n\n플랫폼 수수료 (15%): 적립 시 선차감 완료\n원천징수세 (3.3%): ${tax.toLocaleString()}원\n실수령 예상액: ${netAmount.toLocaleString()}원`,
      { confirmText: "출금 신청" }
    );
    if (confirmed) {
      withdrawMutation.mutate(amount);
    }
  };

  if (isLoading) return <Loading />;

  return (
    <div className="animate-fadeIn flex flex-col gap-5">
      {/* Header */}
      <div>
        <h2 className="text-lg font-extrabold text-achromatic-900">정산</h2>
        <p className="text-xs text-achromatic-400 mt-0.5">수익 및 정산 관리</p>
      </div>

      {/* Balance card */}
      <div className="bg-gradient-to-r from-primary-500 to-violet-500 rounded-2xl p-6 text-white">
        <p className="text-sm font-medium opacity-80 mb-1">출금 가능 금액</p>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-extrabold">{walletBalance.toLocaleString()}</span>
          <span className="text-sm opacity-70">원</span>
        </div>
        {walletBalance < 10000 && (
          <p className="text-[11px] mt-2 opacity-60">
            💡 10,000원 이상일 때 출금할 수 있습니다 (현재 {(10000 - walletBalance).toLocaleString()}원 부족)
          </p>
        )}
      </div>

      {/* Withdrawal section */}
      <div className="bg-white rounded-2xl border border-achromatic-50 p-6">
        <h3 className="text-sm font-bold text-achromatic-900 mb-4">💰 출금하기</h3>

        <div className="flex flex-col gap-3">
          {/* Amount input */}
          <div className="flex gap-2">
            <input
              type="number"
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              placeholder="출금 금액 (원)"
              min={10000}
              className="flex-1 border border-achromatic-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 transition-colors"
            />
            <button
              onClick={handleWithdraw}
              disabled={amount < 10000 || withdrawMutation.isLoading}
              className={`px-5 py-3 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
                amount >= 10000
                  ? "bg-primary-500 text-white hover:bg-primary-600 active:scale-[0.98]"
                  : "bg-achromatic-100 text-achromatic-400 cursor-not-allowed"
              }`}
            >
              {withdrawMutation.isLoading ? "처리 중..." : "출금 신청"}
            </button>
          </div>

          {/* Quick amount buttons */}
          <div className="flex gap-2">
            {[10000, 30000, 50000, 100000].map((v) => (
              <button
                key={v}
                onClick={() => setWithdrawAmount(String(v))}
                disabled={v > walletBalance}
                className={`flex-1 py-2 rounded-lg text-[12px] font-semibold transition-all ${
                  v <= walletBalance
                    ? "bg-achromatic-50 text-achromatic-700 hover:bg-achromatic-100"
                    : "bg-achromatic-50 text-achromatic-300 cursor-not-allowed"
                }`}
              >
                {(v / 10000)}만
              </button>
            ))}
            <button
              onClick={() => setWithdrawAmount(String(walletBalance))}
              disabled={walletBalance < 10000}
              className={`flex-1 py-2 rounded-lg text-[12px] font-semibold transition-all ${
                walletBalance >= 10000
                  ? "bg-primary-50 text-primary-600 hover:bg-primary-100"
                  : "bg-achromatic-50 text-achromatic-300 cursor-not-allowed"
              }`}
            >
              전액
            </button>
          </div>

          {/* Fee breakdown */}
          {amount >= 10000 && (
            <div className="bg-achromatic-50 rounded-xl p-4 flex flex-col gap-2 text-[13px]">
              <div className="flex justify-between text-achromatic-500">
                <span>신청 출금액</span>
                <span>{amount.toLocaleString()}원</span>
              </div>
              <div className="flex justify-between text-achromatic-500">
                <span>플랫폼 수수료 (15%)</span>
                <span className="text-green-600 font-medium">적립 시 선차감 완료</span>
              </div>
              <div className="flex justify-between text-achromatic-500">
                <span>원천징수세 (3.3%)</span>
                <span className="text-red-400">-{tax.toLocaleString()}원</span>
              </div>
              <div className="border-t border-achromatic-200 pt-2 flex justify-between font-bold text-achromatic-900">
                <span>실수령 예상액</span>
                <span className="text-primary-600 text-base">{netAmount.toLocaleString()}원</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Settlement info */}
      <div className="bg-white rounded-2xl border border-achromatic-50 p-6">
        <h3 className="text-sm font-bold text-achromatic-900 mb-3">📋 정산 안내</h3>
        <div className="flex flex-col gap-3 text-[13px] text-achromatic-500">
          <div className="flex items-start gap-3">
            <span className="text-base flex-shrink-0">📅</span>
            <div>
              <p className="font-semibold text-achromatic-700">정산 일정</p>
              <p className="text-xs text-achromatic-400 mt-0.5">매월 <strong>15일</strong>과 <strong>말일</strong>, 월 2회 정산됩니다.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-base flex-shrink-0">💵</span>
            <div>
              <p className="font-semibold text-achromatic-700">최소 출금 금액</p>
              <p className="text-xs text-achromatic-400 mt-0.5">누적 수익이 <strong>10,000원</strong> 이상일 때만 출금 가능합니다.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-base flex-shrink-0">🧾</span>
            <div>
              <p className="font-semibold text-achromatic-700">수수료 및 세금</p>
              <p className="text-xs text-achromatic-400 mt-0.5">플랫폼 수수료 <strong>15%</strong> + 원천징수세 <strong>3.3%</strong>이 공제 후 지급됩니다.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-base flex-shrink-0">🔒</span>
            <div>
              <p className="font-semibold text-achromatic-700">정산 정보 등록</p>
              <p className="text-xs text-achromatic-400 mt-0.5">최초 출금 시 실명·계좌 정보를 등록합니다. 세금 신고에 활용됩니다.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Withdraw History Section */}
      <div className="bg-white rounded-2xl border border-achromatic-50 p-6">
        <h3 className="text-sm font-bold text-achromatic-900 mb-4 flex items-center justify-between">
          <span>📜 출금 및 정산 신청 내역</span>
          <span className="text-xs font-normal text-achromatic-400">총 {withdrawHistory.length}건</span>
        </h3>

        {withdrawHistory.length === 0 ? (
          <div className="text-center py-8 text-xs text-achromatic-400">
            신청하신 출금 내역이 없습니다.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {withdrawHistory.map((item: any) => {
              const statusStr = String(item.status || "");
              const isRejected = statusStr === WalletWithdrawStatus.REJECTED || statusStr === "거절" || statusStr === "REJECTED";
              const isAccepted = statusStr === WalletWithdrawStatus.ACCEPTED || statusStr === "승인" || statusStr === "ACCEPTED";

              return (
                <div
                  key={item.id}
                  className={`p-4 rounded-xl border flex flex-col gap-2 transition-all ${
                    isRejected
                      ? "bg-red-50/60 border-red-100"
                      : isAccepted
                      ? "bg-emerald-50/40 border-emerald-100"
                      : "bg-white border-achromatic-100"
                  }`}
                >
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-achromatic-400 font-mono">
                      {item.createdAt ? new Date(item.createdAt).toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric" }) : "-"}
                    </span>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                        isAccepted
                          ? "bg-emerald-100 text-emerald-700"
                          : isRejected
                          ? "bg-red-100 text-red-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {isAccepted ? "정산 완료 (입금 완료)" : isRejected ? "정산 거절됨 (환불 완료)" : "정산 대기 중"}
                    </span>
                  </div>

                  <div className="flex justify-between items-baseline">
                    <span className="text-xs font-semibold text-achromatic-600">신청 금액</span>
                    <span className="text-base font-extrabold text-achromatic-900">
                      {Number(item.withdrawAmount || 0).toLocaleString()}원
                    </span>
                  </div>

                  {isRejected && (
                    <div className="mt-1 p-3 bg-red-100/80 rounded-xl text-xs text-red-800 flex flex-col gap-1 border border-red-200">
                      <div className="font-bold flex items-center gap-1 text-red-950">
                        <span>❌ 거절 사유:</span>
                        <span>{item.rejectReason || "정산 정보 불일치 (사유 미기재)"}</span>
                      </div>
                      <p className="text-[11px] text-red-700 leading-relaxed">
                        💡 출금 신청 금액 <strong>{Number(item.withdrawAmount || 0).toLocaleString()}원</strong>이 지갑 잔액으로 <strong>자동 소급 환불</strong>되었습니다. 정산 정보를 확인하신 후 다시 신청해 주세요.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* KYC status */}
      <div className="bg-white rounded-2xl border border-achromatic-50 p-6">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-bold text-achromatic-900">🏦 정산 정보</h3>
          <button
            onClick={() => setShowKycModal(true)}
            className="text-[12px] text-primary-500 font-bold hover:text-primary-600 transition-colors"
          >
            {hasKyc ? "수정" : "등록하기"}
          </button>
        </div>
        {hasKyc ? (
          <div className="flex flex-col gap-2 text-[13px]">
            <div className="flex justify-between">
              <span className="text-achromatic-400">실명</span>
              <span className="text-achromatic-700 font-semibold">{user?.realName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-achromatic-400">은행</span>
              <span className="text-achromatic-700 font-semibold">{user?.bankName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-achromatic-400">계좌번호</span>
              <span className="text-achromatic-700 font-semibold">{user?.accountNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-achromatic-400">예금주</span>
              <span className="text-achromatic-700 font-semibold">{user?.accountHolder}</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-[13px] text-achromatic-400 bg-amber-50 p-3 rounded-xl">
            <span>⚠️</span>
            <span>출금을 위해 정산 정보를 등록해주세요.</span>
          </div>
        )}
      </div>

      {/* KYC Modal */}
      {showKycModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 flex flex-col gap-4">
            <h3 className="text-lg font-bold text-achromatic-900">🏦 정산 정보 등록</h3>
            <p className="text-[12px] text-achromatic-400">출금을 위해 아래 정보를 입력해주세요. 세금 신고에 활용됩니다.</p>

            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-achromatic-500">실명</label>
                <input
                  value={kycForm.realName}
                  onChange={(e) => setKycForm({ ...kycForm, realName: e.target.value })}
                  placeholder="홍길동"
                  className="border border-achromatic-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-achromatic-500">주민등록번호</label>
                <input
                  value={kycForm.residentNumber}
                  onChange={(e) => setKycForm({ ...kycForm, residentNumber: formatResidentNumber(e.target.value) })}
                  placeholder="950101-1234567"
                  maxLength={14}
                  className="border border-achromatic-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 font-mono"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-achromatic-500">은행</label>
                <select
                  value={kycForm.bankName}
                  onChange={(e) => setKycForm({ ...kycForm, bankName: e.target.value })}
                  className="border border-achromatic-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 bg-white"
                >
                  <option value="">선택하세요</option>
                  {BANKS.map((bank) => (
                    <option key={bank} value={bank}>{bank}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-achromatic-500">계좌번호</label>
                <input
                  value={kycForm.accountNumber}
                  onChange={(e) => setKycForm({ ...kycForm, accountNumber: e.target.value })}
                  placeholder="계좌번호 (- 없이 숫자만 입력 가능)"
                  className="border border-achromatic-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 font-mono"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-achromatic-500">예금주</label>
                <input
                  value={kycForm.accountHolder}
                  onChange={(e) => setKycForm({ ...kycForm, accountHolder: e.target.value })}
                  placeholder="예금주명"
                  className="border border-achromatic-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500"
                />
              </div>
            </div>

            {/* 법적 개인정보 처리 고지 */}
            <div className="bg-blue-50/80 border border-blue-100 rounded-xl p-3.5 text-[11px] text-blue-900 leading-relaxed">
              <div className="font-bold mb-1 text-blue-950 flex items-center gap-1">
                <span>🔒</span> 개인정보 수집 및 처리 동의 (소득세법 제145조)
              </div>
              <p className="text-blue-800">
                수집된 실명, 주민등록번호, 정산 계좌 정보는 관련 법령에 의거하여 <strong>원천징수 세금 신고(3.3%) 및 정산 대금 이체</strong> 목적으로만 안전하게 사용됩니다.
              </p>
              <p className="text-blue-700 mt-0.5">
                모든 개인정보는 관계 법령에 의거 5년 보관 후 자동 파기되며, 안전하게 암호화되어 관리됩니다.
              </p>
            </div>

            <div className="flex gap-2 mt-2">
              <button
                onClick={() => setShowKycModal(false)}
                className="flex-1 py-3 rounded-xl text-sm font-bold bg-achromatic-100 text-achromatic-600 hover:bg-achromatic-200 transition-colors"
              >
                취소
              </button>
              <button
                onClick={saveKyc}
                className="flex-1 py-3 rounded-xl text-sm font-bold bg-primary-500 text-white hover:bg-primary-600 transition-colors"
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
